import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Printer, BookOpen, Scale, FileText, CheckCircle2, AlertTriangle, ShieldCheck, QrCode, Share2, Copy, Check, Layers, Search, Bookmark } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../services/api';

interface DocumentDetailModalProps {
  document: any;
  onClose: () => void;
  initialViewMode?: 'brief' | 'detailed' | 'sections';
}

export const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({ document: initialDoc, onClose, initialViewMode = 'brief' }) => {
  const [docData, setDocData] = useState<any>(initialDoc);
  const isStatute = Boolean(initialDoc?.legislation_type || initialDoc?.detailed_theme || initialDoc?.notification_number || (initialDoc?.sections && initialDoc.sections.length > 0));
  const effectiveInitialMode = initialViewMode !== 'brief' ? initialViewMode : (isStatute ? 'sections' : 'detailed');
  const [viewMode, setViewMode] = useState<'brief' | 'detailed' | 'sections' | 'headnotes'>(effectiveInitialMode);
  const [fontSize, setFontSize] = useState<number>(15);
  const [copiedFullText, setCopiedFullText] = useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);
  const [copiedSectionIdx, setCopiedSectionIdx] = useState<number | null>(null);
  
  // Section Navigator State
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const [sectionSearchQuery, setSectionSearchQuery] = useState<string>('');
  const [loadingFullSections, setLoadingFullSections] = useState<boolean>(false);

  useEffect(() => {
    setDocData(initialDoc);
    const docId = initialDoc?.id || initialDoc?._id || initialDoc?.slug;
    const docIsStatute = Boolean(initialDoc?.legislation_type || initialDoc?.detailed_theme || initialDoc?.notification_number);
    
    // If it's a statute and doesn't have sections loaded yet, fetch full details
    if (docIsStatute && docId && (!initialDoc?.sections || initialDoc.sections.length === 0)) {
      setLoadingFullSections(true);
      api.get(`/statutes/${encodeURIComponent(docId)}`).then(res => {
        if (res.data) {
          setDocData((prev: any) => ({ ...prev, ...res.data }));
        }
      }).catch(err => {
        console.warn('Could not fetch full statute sections:', err);
      }).finally(() => {
        setLoadingFullSections(false);
      });
    }
  }, [initialDoc]);

  const sectionsList: any[] = useMemo(() => {
    return Array.isArray(docData?.sections) ? docData.sections : [];
  }, [docData]);

  // Filtered sections for search within statute
  const filteredSections = useMemo(() => {
    if (!sectionSearchQuery.trim()) return sectionsList;
    const q = sectionSearchQuery.toLowerCase().trim();
    return sectionsList.filter((s: any) => {
      const num = (s.section_number || '').toString().toLowerCase();
      const title = (s.section_title || '').toLowerCase();
      const text = (s.section_text || '').toLowerCase();
      return num.includes(q) || title.includes(q) || text.includes(q);
    });
  }, [sectionsList, sectionSearchQuery]);

  if (!docData) return null;

  const docId = docData.id || docData._id || docData.slug || docData.citation || 'record';
  const isStatuteDoc = Boolean(docData.legislation_type || docData.detailed_theme || docData.notification_number || docData.sections);
  const sharePath = isStatuteDoc ? `/statutes?id=${encodeURIComponent(docId)}` : `/law-directory?id=${encodeURIComponent(docId)}`;
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${sharePath}` : sharePath;

  const docTitle = docData.title || docData.name || docData.citationNumber || docData.citation || 'Legal Document';
  const docType = docData.legislation_type || docData.type || docData.category || 'Statute / Precedent';
  const docJurisdiction = docData.jurisdiction || docData.province || docData.court || 'Federal / Pakistan';
  const docYear = docData.year || '2026';
  const notifNo = docData.notification_number || docData.sro_number || docData.citationNumber || docData.case_id || '';
  const issuingAuth = docData.issuing_authority || docData.issuing_institution || docData.issuing_body || docData.court || 'Parliament of Pakistan';
  const fullContent = docData.detailed_theme || docData.judgment_text || docData.content || docData.fullText || docData.details || docData.summary || docData.short_description || 'Full text record maintained in ELum Law Repository archives.';

  const activeSection = sectionsList[activeSectionIndex] || sectionsList[0] || null;

  // Generate 4-Bullet Brief Summary
  const bullets = docData.semantic_bullets || docData.bullets || docData.metadata?.ai_5_bullets?.slice(0, 4) || [
    `Enactment/precedent registered under ${docJurisdiction} statutory jurisdiction.`,
    `Establishes mandatory procedures and legal standards for compliance and judicial reference.`,
    `Fully indexed and cross-referenced in ELum Law Directory multi-jurisdictional database with ${sectionsList.length || 'full'} enacted sections.`,
    `Scannable digital reference verified under ELum Law Associates legal repository.`
  ];

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let sectionsHtml = '';
    if (sectionsList.length > 0) {
      sectionsHtml = sectionsList.map((sec: any) => `
        <div style="margin-bottom: 20px; page-break-inside: avoid;">
          <h4 style="color: #0369a1; margin: 0 0 6px 0; font-size: 14px; text-transform: uppercase;">
            ${sec.section_number ? `Section ${sec.section_number}: ` : ''}${sec.section_title || ''}
          </h4>
          <p style="font-size: 13px; line-height: 1.7; color: #1e293b; text-align: justify; margin: 0;">
            ${(sec.section_text || '').replace(/\n/g, '<br/>')}
          </p>
        </div>
      `).join('');
    } else {
      sectionsHtml = `<div style="font-size: 13px; line-height: 1.8; color: #1e293b; text-align: justify;">${fullContent.replace(/\n/g, '<br/>')}</div>`;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${docTitle} - ELum Law Associates</title>
          <style>
            @media print {
              @page { margin: 15mm; size: A4; }
              body { font-family: 'Georgia', 'Times New Roman', serif; color: #0f172a; line-height: 1.6; padding: 0; }
            }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; line-height: 1.6; padding: 25px; max-width: 900px; margin: 0 auto; }
            .header-banner { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #ecc94b; padding-bottom: 15px; margin-bottom: 20px; }
            .logo-section { display: flex; align-items: center; gap: 12px; }
            .logo-img { height: 50px; }
            .firm-name { font-size: 22px; font-weight: bold; color: #0f172a; letter-spacing: 0.5px; }
            .firm-sub { font-size: 11px; color: #0284c7; text-transform: uppercase; font-weight: bold; }
            .doc-type-badge { background: #0f172a; color: #ecc94b; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; }
            .title-box { background: #f8fafc; border-left: 5px solid #0284c7; padding: 15px 20px; border-radius: 8px; margin-bottom: 20px; }
            .doc-title { font-size: 20px; font-weight: bold; color: #0f172a; margin: 0 0 8px 0; }
            .meta-line { font-size: 12px; color: #475569; font-weight: 600; }
            .summary-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 15px 20px; margin-bottom: 25px; }
            .summary-title { font-size: 13px; font-weight: bold; color: #0369a1; text-transform: uppercase; margin-bottom: 10px; }
            .bullet-list { margin: 0; padding-left: 20px; font-size: 13px; color: #1e293b; }
            .bullet-list li { margin-bottom: 6px; }
            .content-box { font-size: 14px; color: #1e293b; line-height: 1.8; margin-bottom: 30px; }
            .footer-banner { border-top: 2px solid #e2e8f0; padding-top: 15px; margin-top: 30px; display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #64748b; }
            .eoe-notice { font-style: italic; font-weight: 600; color: #dc2626; }
          </style>
        </head>
        <body>
          <div class="header-banner">
            <div class="logo-section">
              <img src="/logo_Elum_Firm.png" class="logo-img" alt="ELum Law Logo" onerror="this.src='/logo_ELum_Firm4.png'" />
              <div>
                <div class="firm-name">ELum Law Associates</div>
                <div class="firm-sub">Pakistan's Premier Judicial Directory & Statutory Repository</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 12px;">
              <div style="text-align: right;">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(shareUrl)}" style="width: 54px; height: 54px; border: 1px solid #cbd5e1; border-radius: 4px; padding: 2px; background: white;" alt="Scannable QR Code" />
                <div style="font-size: 8px; color: #64748b; font-weight: bold; margin-top: 2px;">SCAN TO VERIFY</div>
              </div>
              <div class="doc-type-badge">${docType.toUpperCase()}</div>
            </div>
          </div>

          <div class="title-box">
            <h1 class="doc-title">${docTitle}</h1>
            <div class="meta-line">
              Jurisdiction: <strong>${docJurisdiction}</strong> | Year: <strong>${docYear}</strong> ${notifNo ? `| Ref/SRO #: <strong>${notifNo}</strong>` : ''} | Sections: <strong>${sectionsList.length || 'Full'}</strong>
            </div>
          </div>

          <div class="summary-box">
            <div class="summary-title">📌 Executive Summary — Key Highlights</div>
            <ul class="bullet-list">
              ${bullets.map((b: string) => `<li>${b}</li>`).join('')}
            </ul>
          </div>

          <div class="content-box">
            <h3 style="font-size: 15px; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; color: #0f172a;">Statutory Text & Enacted Sections:</h3>
            ${sectionsHtml}
          </div>

          <div class="footer-banner">
            <div>© 2026 ELum Law Associates. All Rights Reserved. | www.elumlaw.com</div>
            <div class="eoe-notice">Errors & Omissions Excepted (E&OE)</div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleDownloadPdf = async () => {
    const docId = docData.slug || docData.id || docData._id;
    if (!docId) {
      handlePrint();
      return;
    }

    setIsDownloadingPdf(true);
    try {
      const isStatute = Boolean(docData.legislation_type || docData.detailed_theme || docData.notification_number || docData.sections);
      const endpoint = isStatute ? `/statutes/${encodeURIComponent(docId)}/pdf` : `/documents/${encodeURIComponent(docId)}/pdf`;
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      const cleanFileName = `${docTitle.replace(/[^a-zA-Z0-9_\-]/g, '_').substring(0, 45)}.pdf`;
      link.setAttribute('download', cleanFileName);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.warn('Direct Blob download failed, attempting window.open fallback:', err);
      const isStatute = Boolean(docData.legislation_type || docData.detailed_theme || docData.notification_number || docData.sections);
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiBase = isLocal ? 'http://localhost:5000/api' : '/api';
      const targetUrl = isStatute ? `${apiBase}/statutes/${encodeURIComponent(docId)}/pdf` : `${apiBase}/documents/${encodeURIComponent(docId)}/pdf`;
      window.open(targetUrl, '_blank');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(6px)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      boxSizing: 'border-box'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        maxWidth: '1050px',
        width: '100%',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        
        {/* MODAL HEADER WITH FIRM LOGO & NAME */}
        <div style={{
          backgroundColor: '#0f172a',
          color: 'white',
          padding: '1.25rem 1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '3px solid #ecc94b'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img 
              src="/logo_Elum_Firm.png" 
              alt="ELum Law Firm" 
              style={{ width: '44px', height: '44px', borderRadius: '8px', border: '2px solid #ecc94b', objectFit: 'cover' }}
              onError={(e: any) => { e.target.src = '/logo_ELum_Firm4.png'; }}
            />
            <div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#ecc94b', letterSpacing: '0.5px' }}>
                ELum Law Associates
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>
                The Premier Judicial Directory & Statutory Node of Pakistan
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '8px',
              padding: '0.5rem',
              color: '#cbd5e1',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* CONTROLS HEADER BAR: VIEW MODES & ACTION BUTTONS */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '0.85rem 1.75rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem', backgroundColor: '#e2e8f0', padding: '4px', borderRadius: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('detailed')}
              style={{
                padding: '0.45rem 1.1rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: viewMode === 'detailed' ? '#0f172a' : 'transparent',
                color: viewMode === 'detailed' ? 'white' : '#475569',
                fontWeight: '700',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <BookOpen size={15} /> Detailed Full Text
            </button>

            {docData?.headnotes && (
              <button
                onClick={() => setViewMode('headnotes')}
                style={{
                  padding: '0.45rem 1.1rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: viewMode === 'headnotes' ? '#166534' : 'transparent',
                  color: viewMode === 'headnotes' ? 'white' : '#475569',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <Scale size={15} /> Headnotes & Ratio
              </button>
            )}

            <button
              onClick={() => setViewMode('brief')}
              style={{
                padding: '0.45rem 1.1rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: viewMode === 'brief' ? '#0284c7' : 'transparent',
                color: viewMode === 'brief' ? 'white' : '#475569',
                fontWeight: '700',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <FileText size={15} /> Executive Brief (4-Bullets)
            </button>

            {sectionsList.length > 0 && (
              <button
                onClick={() => setViewMode('sections')}
                style={{
                  padding: '0.45rem 1.1rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: viewMode === 'sections' ? '#059669' : 'transparent',
                  color: viewMode === 'sections' ? 'white' : '#475569',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <Layers size={15} /> Sections Navigator ({sectionsList.length})
              </button>
            )}
          </div>

          {/* ACTION BUTTONS: FONT SIZE, COPY, PRINT & PDF DOWNLOAD */}
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: '8px' }}>
              <button onClick={() => setFontSize(s => Math.max(12, s - 1))} title="Decrease Text Size" style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: '800', fontSize: '0.82rem', color: '#475569', padding: '0 4px' }}>A-</button>
              <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '700', minWidth: '24px', textAlign: 'center' }}>{fontSize}px</span>
              <button onClick={() => setFontSize(s => Math.min(22, s + 1))} title="Increase Text Size" style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: '800', fontSize: '0.82rem', color: '#475569', padding: '0 4px' }}>A+</button>
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(fullContent);
                setCopiedFullText(true);
                setTimeout(() => setCopiedFullText(false), 2500);
              }}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: copiedFullText ? '#10b981' : 'white',
                color: copiedFullText ? 'white' : '#475569',
                fontWeight: '700',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              {copiedFullText ? <Check size={14} /> : <Copy size={14} />}
              {copiedFullText ? 'Copied Full Text!' : 'Copy Text'}
            </button>

            <button
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isDownloadingPdf ? '#64748b' : '#0284c7',
                color: 'white',
                fontWeight: '700',
                fontSize: '0.82rem',
                cursor: isDownloadingPdf ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                boxShadow: '0 2px 4px rgba(2,132,199,0.25)'
              }}
            >
              <Download size={15} /> {isDownloadingPdf ? 'Generating...' : 'PDF'}
            </button>

            <button
              onClick={handlePrint}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: 'white',
                color: '#475569',
                fontWeight: '700',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              <Printer size={15} /> Print
            </button>
          </div>
        </div>

        {/* MODAL BODY READING PANE */}
        <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', flex: 1, backgroundColor: 'white' }}>
          
          {/* TITLE & THEME METADATA CARD */}
          <div style={{
            backgroundColor: '#f8fafc',
            borderLeft: '5px solid #0284c7',
            padding: '1.1rem 1.25rem',
            borderRadius: '10px',
            marginBottom: '1.25rem',
            border: '1px solid #e2e8f0',
            borderLeftWidth: '5px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.25rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '280px' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <span style={{ backgroundColor: '#0f172a', color: '#ecc94b', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.75rem', borderRadius: '20px', textTransform: 'uppercase' }}>
                    {docType}
                  </span>
                  <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.75rem', borderRadius: '20px' }}>
                    🏛️ {docJurisdiction}
                  </span>
                  <span style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.75rem', borderRadius: '20px' }}>
                    📅 Year: {docYear}
                  </span>
                  {sectionsList.length > 0 && (
                    <span style={{ backgroundColor: '#dcfce7', color: '#166534', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.75rem', borderRadius: '20px' }}>
                      📑 {sectionsList.length} Sections
                    </span>
                  )}
                  {notifNo && (
                    <span style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', fontSize: '0.72rem', fontWeight: '800', padding: '0.25rem 0.75rem', borderRadius: '20px' }}>
                      🔖 SRO / Ref #: {notifNo}
                    </span>
                  )}
                </div>

                <h1 style={{ margin: '0 0 0.35rem 0', fontSize: '1.3rem', fontWeight: '800', color: '#0f172a', lineHeight: '1.4' }}>
                  {docTitle}
                </h1>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Issuing Authority: <strong style={{ color: '#334155' }}>{issuingAuth}</strong>
                </div>
              </div>

              {/* DYNAMIC SCANNABLE QR CODE & INSTANT TRANSFER CARD */}
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                padding: '0.65rem 0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                minWidth: '220px'
              }}>
                <div style={{ backgroundColor: '#f8fafc', padding: '4px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <QRCodeSVG value={shareUrl} size={68} level="M" includeMargin={false} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <QrCode size={13} color="#0284c7" /> Scan & Share
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b', lineHeight: '1.2', maxWidth: '130px' }}>
                    Verify & open statute on mobile.
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      setCopiedLink(true);
                      setTimeout(() => setCopiedLink(false), 2500);
                    }}
                    style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: '6px',
                      border: '1px solid #0284c7',
                      backgroundColor: copiedLink ? '#10b981' : '#f0f9ff',
                      color: copiedLink ? 'white' : '#0284c7',
                      fontSize: '0.65rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {copiedLink ? <Check size={11} /> : <Copy size={11} />}
                    {copiedLink ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* BRIEF DETAILS VIEW (4 BULLETS SUMMARY) */}
          {viewMode === 'brief' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ backgroundColor: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd', padding: '1.25rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: '800', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <ShieldCheck size={18} color="#0284c7" /> Executive Brief — Core Legislative Highlights
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {bullets.map((b: string, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', backgroundColor: 'white', padding: '0.8rem 1rem', borderRadius: '8px', border: '1px solid #e0f2fe' }}>
                      <CheckCircle2 size={17} color="#0284c7" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: '1.5', fontWeight: '500' }}>
                        {b}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {sectionsList.length > 0 ? (
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#ecfdf5', borderRadius: '10px', border: '1px dashed #6ee7b7' }}>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#065f46', fontWeight: '600' }}>
                    This statute contains <strong>{sectionsList.length} enacted sections</strong>. Click <strong>"Sections Navigator"</strong> above to browse section by section!
                  </p>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#faf5ff', borderRadius: '10px', border: '1px dashed #c084fc' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b21a8', fontWeight: '600' }}>
                    To view the complete transcript, click the <strong>"Detailed Full Text"</strong> tab above or use <strong>"Print / Save PDF"</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SECTIONS NAVIGATOR VIEW */}
          {viewMode === 'sections' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: '1.25rem', minHeight: '400px' }}>
              {/* Left Column: Sections Index List */}
              <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #cbd5e1', padding: '0.85rem', display: 'flex', flexDirection: 'column', height: '480px' }}>
                <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                  <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    value={sectionSearchQuery}
                    onChange={(e) => setSectionSearchQuery(e.target.value)}
                    placeholder="Search sections..."
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem 0.45rem 2rem',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingRight: '4px' }}>
                  {filteredSections.map((sec: any, idx: number) => {
                    const originalIdx = sectionsList.indexOf(sec);
                    const isSelected = originalIdx === activeSectionIndex;
                    const secLabel = sec.section_number 
                      ? `Sec. ${sec.section_number}: ${sec.section_title || 'Section'}`
                      : (sec.section_title || `Section ${idx + 1}`);

                    return (
                      <button
                        key={idx}
                        onClick={() => setActiveSectionIndex(originalIdx >= 0 ? originalIdx : 0)}
                        style={{
                          textAlign: 'left',
                          padding: '0.55rem 0.75rem',
                          borderRadius: '6px',
                          border: isSelected ? '2px solid #059669' : '1px solid #e2e8f0',
                          backgroundColor: isSelected ? '#ecfdf5' : 'white',
                          color: isSelected ? '#065f46' : '#1e293b',
                          fontWeight: isSelected ? '700' : '500',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '0.4rem',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {secLabel}
                        </span>
                        {sec.word_count ? (
                          <span style={{ fontSize: '0.65rem', backgroundColor: isSelected ? '#a7f3d0' : '#f1f5f9', color: '#475569', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>
                            {sec.word_count}w
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {filteredSections.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                      No matching sections found.
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Selected Section Text Card */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #cbd5e1', padding: '1.25rem', display: 'flex', flexDirection: 'column', height: '480px', overflowY: 'auto' }}>
                {activeSection ? (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <span style={{ backgroundColor: '#059669', color: 'white', fontSize: '0.72rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '12px', textTransform: 'uppercase' }}>
                          {activeSection.section_number ? `Section ${activeSection.section_number}` : 'Enacted Text'}
                        </span>
                        <h2 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#0f172a', margin: '0.4rem 0 0 0' }}>
                          {activeSection.section_title || docTitle}
                        </h2>
                      </div>

                      <button
                        onClick={() => {
                          const quoteText = `"${activeSection.section_text}"\n— ${docTitle}, Section ${activeSection.section_number || ''} (${docJurisdiction})`;
                          navigator.clipboard.writeText(quoteText);
                          setCopiedSectionIdx(activeSectionIndex);
                          setTimeout(() => setCopiedSectionIdx(null), 2500);
                        }}
                        style={{
                          padding: '0.35rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: copiedSectionIdx === activeSectionIndex ? '#10b981' : '#f8fafc',
                          color: copiedSectionIdx === activeSectionIndex ? 'white' : '#334155',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        {copiedSectionIdx === activeSectionIndex ? <Check size={13} /> : <Copy size={13} />}
                        {copiedSectionIdx === activeSectionIndex ? 'Section Copied!' : 'Copy Section Text'}
                      </button>
                    </div>

                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '0.94rem', color: '#1e293b', textAlign: 'justify' }}>
                      {activeSection.section_text || 'No text recorded for this section.'}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '0.9rem' }}>
                    Select a section from the index on the left to read.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HEADNOTES & RATIO DECIDENDI VIEW */}
          {viewMode === 'headnotes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #86efac', borderLeft: '6px solid #16a34a', padding: '1.5rem', boxShadow: '0 2px 6px rgba(22, 163, 74, 0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #bbf7d0', paddingBottom: '0.6rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Scale size={20} color="#16a34a" /> Legal Headnotes & Ratio Decidendi
                  </h3>
                  <span style={{ fontSize: '0.75rem', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '800', padding: '0.25rem 0.65rem', borderRadius: '12px' }}>
                    Authoritative Points of Law Decided
                  </span>
                </div>

                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.85', fontSize: `${fontSize}px`, color: '#14532d', textAlign: 'justify', fontFamily: "'Georgia', 'Cambria', serif" }}>
                  {docData.headnotes || 'No separate headnotes extracted for this precedent. Please refer to Detailed Full Text tab.'}
                </div>
              </div>

              <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                  To examine the full judicial opinion, orders, and evidence analysis, switch to the <strong>"Detailed Full Text"</strong> tab above.
                </p>
              </div>
            </div>
          )}

          {/* DETAILED FULL TEXT VIEW */}
          {viewMode === 'detailed' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* Headnotes Highlight Banner if available */}
              {docData?.headnotes && (
                <div style={{ backgroundColor: '#f0fdf4', borderRadius: '10px', padding: '1.1rem 1.25rem', border: '1px solid #86efac', borderLeft: '5px solid #16a34a' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, fontSize: '0.88rem', fontWeight: '800', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.4rem', textTransform: 'uppercase' }}>
                      <Scale size={16} color="#16a34a" /> Legal Ratio Decidendi & Headnotes:
                    </h4>
                    <span style={{ fontSize: '0.7rem', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '700', padding: '1px 6px', borderRadius: '4px' }}>
                      Points of Law
                    </span>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.75', fontSize: `${Math.max(13, fontSize - 1)}px`, color: '#14532d', textAlign: 'justify' }}>
                    {docData.headnotes}
                  </div>
                </div>
              )}

              {/* Complete Statutory Sections OR Verbatim Precedent Judgment */}
              {sectionsList.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {sectionsList.map((sec: any, i: number) => (
                    <div key={i} style={{ backgroundColor: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#0369a1', textTransform: 'uppercase' }}>
                          {sec.section_number ? `Section ${sec.section_number}: ` : ''}{sec.section_title || ''}
                        </h4>
                        {sec.word_count && (
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{sec.word_count} words</span>
                        )}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '0.92rem', color: '#1e293b', textAlign: 'justify' }}>
                        {sec.section_text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #0f172a', paddingBottom: '0.65rem', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <BookOpen size={18} color="#0284c7" /> Complete Verbatim Judgment & Order:
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: '700', padding: '0.2rem 0.65rem', borderRadius: '12px' }}>
                        📖 {docData.wordCount ? `${docData.wordCount.toLocaleString()} words` : `${Math.round(fullContent.length / 6).toLocaleString()} words`} (~{Math.max(1, Math.round((docData.wordCount || (fullContent.length / 6)) / 450))} pages)
                      </span>
                      <span style={{ fontSize: '0.75rem', backgroundColor: '#dcfce7', color: '#166534', fontWeight: '800', padding: '0.2rem 0.65rem', borderRadius: '12px' }}>
                        ✓ Authentic Record
                      </span>
                    </div>
                  </div>

                  <div style={{ 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: '1.9', 
                    fontSize: `${fontSize}px`, 
                    color: '#0f172a', 
                    textAlign: 'justify',
                    fontFamily: "'Georgia', 'Cambria', 'Times New Roman', serif",
                    wordBreak: 'break-word'
                  }}>
                    {fullContent}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* MODAL FOOTER WITH COPYRIGHT & E&OE NOTICE */}
        <div style={{
          backgroundColor: '#f1f5f9',
          padding: '0.85rem 1.75rem',
          borderTop: '1px solid #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.78rem',
          color: '#475569',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <div>
            © 2026 <strong>ELum Law Associates</strong>. All Rights Reserved. | <a href="https://www.elumlaw.com" target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'none', fontWeight: '700' }}>www.elumlaw.com</a>
          </div>
          <div style={{ color: '#dc2626', fontWeight: '700', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <AlertTriangle size={14} color="#dc2626" /> Errors & Omissions Excepted (E&OE)
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocumentDetailModal;
